/*
 * Related Products V2 carousel runtime.
 *
 * Extraction of the three production inline <script> blocks that previously
 * lived in sections/related-products-carousel-v2.liquid (theme-fixlist-2026-05-11
 * §A4):
 *   1. Async recommendations loader (fetches /recommendations/products and
 *      swaps the section innerHTML when a manual product list is not in use)
 *   2. Carousel chrome — arrows, smooth-scroll snap, click-and-drag with
 *      momentum, capture-phase click suppression after drags
 *   3. Progress-bar updater — RAF loop that paints the scroll position
 *      as a two-tone linear-gradient on the .rpc-pbar element
 *
 * Per-section config lives on the .rpc section root via data attributes:
 *   id="rpc-<sid>"                — section id baked into the element id
 *   data-section-id               — Shopify section id
 *   data-product-id               — current product id
 *   data-needs-rec                — "true" → trigger the async loader
 *   data-rec-limit                — recommendations.products limit
 *   data-pbar-track-color         — progress-bar track color
 *   data-pbar-thumb-color         — progress-bar thumb color
 *
 * The progress-bar element is found by suffix: #rpc-pbar-<sid>. Multiple
 * instances on the same page are supported (each gets its own RAF loop).
 *
 * The JSON-LD ItemList structured-data <script> stays inline (data only).
 */
(function () {
  'use strict';

  function ridFromSection(section) {
    return section.id; // e.g. "rpc-abc123"
  }

  function sidFromSection(section) {
    var rid = section.id || '';
    return rid.replace(/^rpc-/, '');
  }

  /* ──────────────────────────────────────────
   * 1. Async recommendations loader
   * ────────────────────────────────────────── */
  function loadRecommendations(section) {
    if (section.getAttribute('data-needs-rec') !== 'true') return;
    if (section.dataset.rpcRecLoading === '1') return;
    section.dataset.rpcRecLoading = '1';

    var productId = section.getAttribute('data-product-id');
    var sectionId = section.getAttribute('data-section-id');
    var limit = section.getAttribute('data-rec-limit') || 8;
    if (!productId || !sectionId) return;

    var url =
      '/recommendations/products?product_id=' + productId +
      '&section_id=' + sectionId +
      '&limit=' + limit;

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var fresh = temp.querySelector('#' + section.id);
        if (fresh) {
          section.innerHTML = fresh.innerHTML;
          section.removeAttribute('data-rpc-ready');
          section.setAttribute('data-needs-rec', 'false');
          // hcs-product-card.js uses document-level delegation, so newly
          // injected cards are picked up automatically. The carousel chrome
          // needs an explicit re-init though — that's wired below.
          document.dispatchEvent(new CustomEvent('rpc:loaded', { detail: { rid: section.id } }));
        }
      })
      .catch(function () {
        // Fetch failed — leave the existing markup as-is.
      });
  }

  /* ──────────────────────────────────────────
   * 2. Carousel chrome — arrows, drag, snap
   * ────────────────────────────────────────── */
  function initCarouselChrome(section) {
    if (section.dataset.rpcReady) return;
    section.dataset.rpcReady = '1';

    var track = section.querySelector('[data-rpc-track]');
    var arrowL = section.querySelector('[data-rpc-arrow="left"]');
    var arrowR = section.querySelector('[data-rpc-arrow="right"]');
    if (!track) return;

    var snapAnims = new Map();

    function smoothScrollTo(el, target, dur) {
      var prev = snapAnims.get(el);
      if (prev) cancelAnimationFrame(prev);
      var start = el.scrollLeft;
      var diff = target - start;
      var startTime = null;
      function step(ts) {
        if (!startTime) startTime = ts;
        var p = Math.min((ts - startTime) / dur, 1);
        var ease = p < 0.5 ? 2 * p * p : -1 + (4 - 2 * p) * p;
        el.scrollLeft = start + diff * ease;
        if (p < 1) {
          snapAnims.set(el, requestAnimationFrame(step));
        } else {
          snapAnims.delete(el);
          syncAll();
        }
      }
      snapAnims.set(el, requestAnimationFrame(step));
    }

    function findNearest(tr) {
      var cards = tr.querySelectorAll('.hcs-card');
      var cur = tr.scrollLeft;
      var best = null;
      var bestDist = Infinity;
      cards.forEach(function (c) {
        var snap = c.offsetLeft - parseInt(getComputedStyle(tr).paddingLeft || 0, 10);
        var d = Math.abs(snap - cur);
        if (d < bestDist) { bestDist = d; best = snap; }
      });
      return best !== null ? best : 0;
    }

    function findDirectional(tr, dir) {
      var cards = tr.querySelectorAll('.hcs-card');
      var cur = tr.scrollLeft;
      var best = null;
      if (dir > 0) {
        cards.forEach(function (c) {
          var snap = c.offsetLeft - parseInt(getComputedStyle(tr).paddingLeft || 0, 10);
          if (snap > cur + 2 && (best === null || snap < best)) best = snap;
        });
      } else {
        cards.forEach(function (c) {
          var snap = c.offsetLeft - parseInt(getComputedStyle(tr).paddingLeft || 0, 10);
          if (snap < cur - 2 && (best === null || snap > best)) best = snap;
        });
      }
      return best !== null ? best : findNearest(tr);
    }

    function syncArrows() {
      if (arrowL) arrowL.style.display = track.scrollLeft <= 2 ? 'none' : '';
      if (arrowR) {
        arrowR.style.display =
          track.scrollLeft + track.offsetWidth >= track.scrollWidth - 2 ? 'none' : '';
      }
    }
    function syncAll() { syncArrows(); }

    if (arrowL) arrowL.addEventListener('click', function () {
      smoothScrollTo(track, findDirectional(track, -1), 400);
    });
    if (arrowR) arrowR.addEventListener('click', function () {
      smoothScrollTo(track, findDirectional(track, 1), 400);
    });
    track.addEventListener('scroll', syncAll, { passive: true });
    syncAll();
    window.addEventListener('resize', syncAll);

    // Desktop click-and-drag with momentum + capture-phase click suppression
    (function (el) {
      var isDown = false;
      var startX = 0;
      var scrollL = 0;
      var hasDragged = false;
      var lastX = 0;
      var lastTime = 0;
      var velocity = 0;
      var DRAG_THRESHOLD = 5;

      el.addEventListener('dragstart', function (e) { e.preventDefault(); });

      el.addEventListener('mousedown', function (e) {
        if (e.button !== 0) return;
        // Don't steal the user's tap on the ATC button / panel — hcs-product-card.js
        // handles those interactions via document delegation.
        if (e.target.closest('.hcs-card__atb-area, [data-hcs-atb-wrap], .hcs-card__atb-btn')) return;
        var prev = snapAnims.get(el);
        if (prev) cancelAnimationFrame(prev);
        isDown = true;
        hasDragged = false;
        velocity = 0;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
        startX = e.pageX - el.offsetLeft;
        scrollL = el.scrollLeft;
        lastX = e.pageX;
        lastTime = Date.now();
      });

      function endDrag() {
        if (!isDown) return;
        isDown = false;
        el.style.cursor = 'grab';
        el.style.userSelect = '';
        if (Math.abs(velocity) > 0.5) {
          var v = velocity;
          var friction = 0.95;
          function coast() {
            v *= friction;
            el.scrollLeft += v;
            syncAll();
            if (Math.abs(v) > 0.5) {
              snapAnims.set(el, requestAnimationFrame(coast));
            } else {
              snapAnims.delete(el);
            }
          }
          snapAnims.set(el, requestAnimationFrame(coast));
        }
      }

      el.addEventListener('mouseleave', endDrag);
      el.addEventListener('mouseup', endDrag);
      el.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        var dx = Math.abs(e.pageX - (startX + el.offsetLeft));
        if (!hasDragged && dx < DRAG_THRESHOLD) return;
        e.preventDefault();
        hasDragged = true;
        var x = e.pageX - el.offsetLeft;
        el.scrollLeft = scrollL - (x - startX);
        var now = Date.now();
        var dt = now - lastTime;
        if (dt > 0) {
          velocity = ((lastX - e.pageX) / dt) * 16;
          lastX = e.pageX;
          lastTime = now;
        }
      });

      // Capture-phase so we beat hcs-product-card.js's click handler when a
      // drag has occurred — but only then. Normal clicks pass through.
      el.addEventListener(
        'click',
        function (e) {
          if (hasDragged) {
            e.preventDefault();
            e.stopPropagation();
            hasDragged = false;
          }
        },
        true
      );
    })(track);
  }

  /* ──────────────────────────────────────────
   * 3. Progress-bar updater (RAF loop, two-tone gradient)
   * ────────────────────────────────────────── */
  function initProgressBar(section) {
    var sid = sidFromSection(section);
    var el = document.getElementById('rpc-pbar-' + sid);
    if (!el) return;
    if (el.dataset.rpcPbarReady === '1') return;
    el.dataset.rpcPbarReady = '1';

    var tc = section.getAttribute('data-pbar-track-color') || '#E5D9C9';
    var th = section.getAttribute('data-pbar-thumb-color') || '#2E2A25';

    function getTrack() {
      var s = document.getElementById(ridFromSection(section));
      return s ? s.querySelector('[data-rpc-track]') : null;
    }

    function tick() {
      var t = getTrack();
      if (t) {
        var sw = t.scrollWidth;
        var cw = t.offsetWidth;
        if (sw > cw + 2) {
          el.style.opacity = '1';
          var ratio = cw / sw;
          var thumbW = Math.max(ratio * 100, 15);
          var maxScroll = sw - cw;
          var pct = maxScroll > 0 ? Math.min(t.scrollLeft / maxScroll, 1) : 0;
          var left = pct * (100 - thumbW);
          var right = left + thumbW;
          el.style.background =
            'linear-gradient(to right,' + tc + ' ' + left + '%,' +
            th + ' ' + left + '%,' + th + ' ' + right + '%,' +
            tc + ' ' + right + '%)';
        } else {
          el.style.opacity = '0';
        }
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ──────────────────────────────────────────
   * Boot
   * ────────────────────────────────────────── */
  function initSection(section) {
    loadRecommendations(section);
    initCarouselChrome(section);
    initProgressBar(section);
  }

  function initAll() {
    document.querySelectorAll('.rpc[id^="rpc-"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () {
    initAll();
  });

  // Re-init after async recommendations loader swaps in new DOM. We can't
  // assume the original section element is still the same node, so re-query.
  document.addEventListener('rpc:loaded', function (e) {
    var rid = e.detail && e.detail.rid;
    if (!rid) { initAll(); return; }
    var section = document.getElementById(rid);
    if (section) {
      section.removeAttribute('data-rpc-ready');
      delete section.dataset.rpcRecLoading;
      initSection(section);
    }
  });
})();
